const IFRAME_RESIZE_MESSAGE_TYPE = 'iframeResize'
const DEBOUNCE_DELAY = 100 // ms

/**
 * Debounce helper function
 */
function debounce (callback: (height: number) => void): (height: number) => void {
  let debounceTimer: ReturnType<typeof setTimeout> | undefined

  return (height: number): void => {
    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer)
    }

    debounceTimer = setTimeout(() => {
      callback(height)
    }, DEBOUNCE_DELAY)
  }
}

/**
 * Child component for iframe resizer scripts
 */
function iframeResizerChild (): void {
  if (typeof window === 'undefined' || window.self === window.top) {
    return
  }

  function postMessageToParent (height: number): void {
    parent.postMessage({ type: IFRAME_RESIZE_MESSAGE_TYPE, height }, '*')
  }

  const sendDebouncedHeightMessage = debounce(postMessageToParent)

  // Send updated height to parent if size change is greater than the threshold
  const resizeUpdate = (): void => {
    if (typeof window === 'undefined') {
      return
    }

    const newHeight = window.document?.body?.scrollHeight

    if (newHeight == null) {
      return
    }

    sendDebouncedHeightMessage(newHeight)
  }

  function initializeResizeObserver (): void {
    const resizeObserver = new ResizeObserver(resizeUpdate)

    // Set initial height on page load, then start resizeObserver
    resizeUpdate()
    resizeObserver.observe(window.document.body)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeResizeObserver)
  } else {
    initializeResizeObserver()
  }
}

export {
  iframeResizerChild,
  IFRAME_RESIZE_MESSAGE_TYPE
}
